import React, { useState, useEffect } from "react";
import { MakeApiCall } from "../../../../apis";
import { Upload, message, Input, Button } from "antd";
import {
    PlusOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    LockOutlined,
    CheckCircleOutlined,
    CameraOutlined
} from "@ant-design/icons";
import { Wrapper } from "./style";

const MyProfile = ({ setData, userData }) => {
    const Id = userData?.user?.user_data?.id;

    // --- Profile States ---
    const [isEditing, setIsEditing] = useState(false);
    const [profile, setProfile] = useState(null);
    const [editedProfile, setEditedProfile] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [newImageFile, setNewImageFile] = useState(null);

    // --- Password States ---
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        old_password: "",
        new_password: "",
        confirm_password: ""
    });
    const [loadingPassword, setLoadingPassword] = useState(false);

    useEffect(() => {
        const getProfileDetails = async () => {
            const response = await MakeApiCall(`user/${Id}`, "get", null, true, {}, userData?.token);
            if (response?.status) {
                setProfile(response?.data || {});
            } else {
                message.warning(response?.message);
            }
        };
        if (Id) getProfileDetails();
    }, [Id, userData?.token]);

    // --- Profile Handlers ---
    const beforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            message.error("You can only upload image files!");
            return Upload.LIST_IGNORE;
        }
        return false;
    };

    const handleProfilePictureChange = ({ file }) => {
        const rawFile = file.originFileObj || file;
        if (rawFile) {
            setNewImageFile(rawFile);
            setImageChanged(true);
            const previewURL = URL.createObjectURL(rawFile);
            // We update the local state so the preview shows in the circle immediately
            setProfile((prev) => ({ ...prev, photo: previewURL }));
        }
    };

    const handleEditToggle = async () => {
        if (isEditing) {
            let finalProfileToSet = { ...editedProfile };
            let hasChanges = JSON.stringify(profile) !== JSON.stringify(editedProfile);

            // If photo was changed via the hover trigger, we handle it during Save
            if (imageChanged && newImageFile) {
                const formData = new FormData();
                formData.append("User[photo]", newImageFile);

                const imgRes = await MakeApiCall(
                    `user/${Id}/update-image`,
                    "post",
                    formData,
                    true,
                    { "Content-Type": undefined },
                    userData?.token
                );

                if (imgRes?.status) {
                    message.success("Profile picture updated!");
                    finalProfileToSet.photo = imgRes?.data?.photo;
                    hasChanges = true;
                } else {
                    message.error("Image update failed.");
                    return;
                }
            }

            if (hasChanges) {
                const { photo, ...payloadWithoutPhoto } = finalProfileToSet;
                const response = await MakeApiCall(`user/${Id}`, "put", payloadWithoutPhoto, true, { "Content-Type": "application/json" }, userData?.token);

                if (response?.status) {
                    message.success("Profile updated successfully!");
                    setProfile(finalProfileToSet);
                    if (typeof setData === "function") {
                        setData({ ...userData, user: { ...userData.user, user_data: { ...userData.user.user_data, ...finalProfileToSet } } });
                    }
                }
            }
            setImageChanged(false);
            setNewImageFile(null);
        } else {
            setEditedProfile({ ...profile });
        }
        setIsEditing(!isEditing);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedProfile((prev) => ({ ...prev, [name]: value }));
    };

    // --- Password Handlers ---
    const handlePasswordUpdate = async () => {
        const { old_password, new_password, confirm_password } = passwordData;
        if (!old_password || !new_password || !confirm_password) return message.error("Fill all fields");
        if (new_password !== confirm_password) return message.error("Passwords mismatch");

        setLoadingPassword(true);
        const response = await MakeApiCall(`user/${Id}/change-password`, "put", { old_password, new_password }, true, { "Content-Type": "application/json" }, userData?.token);
        setLoadingPassword(false);
        if (response?.status) {
            message.success("Password updated!");
            setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
            setIsChangingPassword(false);
        }
    };

    if (!profile) return null;

    return (
        <Wrapper>
            <style>{`
                .profile-avatar-wrapper {
                    position: relative;
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    overflow: hidden;
                    cursor: pointer;
                    border: 4px solid #f8f9fa;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .avatar-overlay {
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(0,0,0,0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    color: white;
                    font-size: 24px;
                }
                .profile-avatar-wrapper:hover .avatar-overlay {
                    opacity: 1;
                }
            `}</style>

            <div className="container-fluid mt-5">
                <div className="row g-4 d-flex align-items-stretch justify-content-center">

                    {/* LEFT PANEL */}
                    <div className="col-md-4 col-xxl-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-body d-flex flex-column align-items-center justify-content-center text-center py-10">

                                {/* AVATAR WITH HOVER UPLOAD */}
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                    onChange={handleProfilePictureChange}
                                    disabled={!isEditing}
                                >
                                    <div className="profile-avatar-wrapper mb-5">
                                        <img src={profile?.photo || "https://via.placeholder.com/120"} className="w-100 h-100 object-fit-cover" alt="avatar" />
                                        {isEditing && (
                                            <div className="avatar-overlay">
                                                <CameraOutlined />
                                            </div>
                                        )}
                                    </div>
                                </Upload>

                                <h3 className="fw-bolder text-dark mb-1">{profile.name}</h3>
                                <div className="d-flex align-items-center text-muted fs-7 mb-6">
                                    <CheckCircleOutlined className="text-success me-1" /> Verified Account
                                </div>

                                <div className="w-100 px-4 mb-8 text-start">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-light-primary p-2 rounded-3 me-3"><MailOutlined className="text-primary" /></div>
                                        <span className="text-gray-600 fw-semibold text-truncate">{profile.email}</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-light-success p-2 rounded-3 me-3"><PhoneOutlined className="text-success" /></div>
                                        <span className="text-gray-600 fw-semibold">{profile.contact_no || "N/A"}</span>
                                    </div>
                                </div>

                                <button
                                    className={`btn btn-sm w-100 fw-bold transition-all ${isChangingPassword ? 'btn-light' : 'btn-light-danger'}`}
                                    onClick={() => { setIsChangingPassword(!isChangingPassword); setIsEditing(false); }}
                                >
                                    <LockOutlined className="me-2" />
                                    {isChangingPassword ? "Back to Profile" : "Change Password"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT PANEL */}
                    <div className="col-md-8 col-xxl-6">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-transparent">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bolder fs-3 text-dark">{isChangingPassword ? "Security Settings" : "Personal Information"}</span>
                                    <span className="text-muted mt-1 fw-bold fs-7">{isChangingPassword ? "Update password" : "Edit your basic account details"}</span>
                                </h3>
                                {!isChangingPassword && (
                                    <button
                                        className={`btn btn-sm fw-bold px-6 py-2 transition-all ${isEditing ? "btn-danger" : "btn-light-danger"}`}
                                        onClick={handleEditToggle}
                                        style={{ borderRadius: "8px" }}
                                    >
                                        {isEditing ? "Save Profile" : "Edit Profile"}
                                    </button>
                                )}
                            </div>

                            <div className="card-body">
                                {isChangingPassword ? (
                                    /* PASSWORD FORM (Unchanged) */
                                    <div className="py-2">
                                        <PasswordRow label="Current Password" name="old_password" value={passwordData.old_password} onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})} />
                                        <PasswordRow label="New Password" name="new_password" value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} />
                                        <PasswordRow label="Confirm Password" name="confirm_password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} />
                                        <div className="d-flex justify-content-end">
                                            <Button type="primary" danger size="large" loading={loadingPassword} onClick={handlePasswordUpdate} className="px-10 fw-bold" style={{ borderRadius: '8px' }}>Update Password</Button>
                                        </div>
                                    </div>
                                ) : (
                                    /* PROFILE FORM (Image Upload Removed from here) */
                                    <form onSubmit={(e) => e.preventDefault()}>
                                        <FormRow label="Full Name" name="name" icon={<UserOutlined />} value={isEditing ? editedProfile?.name : profile?.name} isEditing={isEditing} onChange={handleChange} />
                                        <FormRow label="Email" name="email" icon={<MailOutlined />} value={isEditing ? editedProfile?.email : profile?.email} isEditing={isEditing} onChange={handleChange} />
                                        <FormRow label="Contact" name="contact_no" icon={<PhoneOutlined />} value={isEditing ? editedProfile?.contact_no : profile?.contact_no} isEditing={isEditing} onChange={handleChange} />
                                        <FormRow label="Address" name="address" icon={<HomeOutlined />} value={isEditing ? editedProfile?.address : profile?.address} isEditing={isEditing} onChange={handleChange} />
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const FormRow = ({ label, name, value, isEditing, onChange, icon }) => (
    <div className="row mb-6 align-items-center">
        <label className="col-lg-4 col-form-label fw-bold fs-6 text-muted">{label}</label>
        <div className="col-lg-8">
            <div className={`input-group ${!isEditing ? "input-group-flush border-0" : ""}`}>
                {isEditing && <span className="input-group-text bg-light border-end-0">{icon}</span>}
                <input
                    type="text"
                    name={name}
                    value={value || ""}
                    readOnly={!isEditing}
                    onChange={onChange}
                    className={`form-control ${isEditing ? "bg-light-primary border-primary" : "bg-transparent border-0 fw-bold fs-6 text-dark"}`}
                    style={{ paddingLeft: isEditing ? "12px" : "0", borderRadius: isEditing ? "0 8px 8px 0" : "0" }}
                />
            </div>
        </div>
    </div>
);

const PasswordRow = ({ label, name, value, onChange }) => (
    <div className="row mb-6">
        <label className="col-lg-4 col-form-label fw-bold text-muted">{label}</label>
        <div className="col-lg-8"><Input.Password name={name} size="large" value={value} onChange={onChange} /></div>
    </div>
);

export default MyProfile;